import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const DEFAULT_EMAIL = 'contato@kairosseminarios.com.br';

const PoliticaPrivacidadePage = () => {
  const [emailLgpd, setEmailLgpd] = useState(DEFAULT_EMAIL);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/config/site`);
        if (response.ok) {
          const data = await response.json();
          if (data.email_lgpd) {
            setEmailLgpd(data.email_lgpd);
          }
        }
      } catch (error) {
        console.error('Error fetching site config:', error);
        // Fallback to default email
      }
    };
    fetchConfig();
  }, []);

  return (
    <>
      <Helmet>
        <title>Política de Privacidade (LGPD) | Kairós</title>
        <meta name="description" content="Política de privacidade e tratamento de dados pessoais (LGPD) do Kairós." />
      </Helmet>

      <div className="min-h-screen bg-obsidian" data-testid="politica-privacidade-page">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-obsidian/90 backdrop-blur-md border-b border-white/5">
          <div className="container mx-auto px-4 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <ArrowLeft className="w-5 h-5 text-gray-400" />
              <img src="/logo-kairos.png" alt="Kairós" className="w-10 h-10 rounded-full object-cover" />
              <span className="font-display text-xl text-white">Kairós</span>
            </Link>
          </div>
        </nav>

        {/* Content */}
        <main className="pt-24 pb-20">
          <div className="container mx-auto px-4 max-w-4xl">
            <article className="prose prose-invert prose-lg max-w-none">
              <p className="text-gray-400 text-sm mb-2">Última atualização: 01 de março de 2026</p>
              
              <h1 className="font-display text-3xl md:text-4xl text-white mb-8">
                Política de Privacidade (LGPD)
              </h1>

              <section className="mb-10">
                <h2 className="text-xl md:text-2xl text-white font-semibold mb-4">1. Dados Coletados</h2>
                <p className="text-gray-300 leading-relaxed mb-4">Podemos coletar:</p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                  <li>Nome completo;</li>
                  <li>CPF;</li>
                  <li>Data de nascimento;</li>
                  <li>Endereço;</li>
                  <li>Telefone;</li>
                  <li>E-mail;</li>
                  <li>Dados do cônjuge;</li>
                  <li>Informações de pagamento (processadas via Mercado Pago).</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-xl md:text-2xl text-white font-semibold mb-4">2. Finalidade</h2>
                <p className="text-gray-300 leading-relaxed mb-4">Os dados são utilizados para:</p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                  <li>Confirmar inscrição;</li>
                  <li>Emitir documentos fiscais (quando aplicável);</li>
                  <li>Organizar hospedagem;</li>
                  <li>Comunicação sobre o evento;</li>
                  <li>Cumprimento de obrigações legais.</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-xl md:text-2xl text-white font-semibold mb-4">3. Base Legal</h2>
                <p className="text-gray-300 leading-relaxed mb-4">Tratamento baseado em:</p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                  <li>Execução de contrato;</li>
                  <li>Cumprimento de obrigação legal;</li>
                  <li>Consentimento do titular (quando aplicável).</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-xl md:text-2xl text-white font-semibold mb-4">4. Armazenamento e Segurança</h2>
                <p className="text-gray-300 leading-relaxed">
                  Os dados são armazenados em ambiente seguro e protegidos por medidas técnicas adequadas.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-xl md:text-2xl text-white font-semibold mb-4">5. Direitos do Titular</h2>
                <p className="text-gray-300 leading-relaxed mb-4">O participante pode solicitar:</p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                  <li>Acesso aos dados;</li>
                  <li>Correção;</li>
                  <li>Exclusão (quando aplicável);</li>
                  <li>Revogação de consentimento.</li>
                </ul>
                <div className="mt-6 p-4 bg-white/5 border border-white/10 rounded-lg">
                  <p className="text-gray-300">
                    <strong className="text-white">Canal de contato do controlador:</strong><br />
                    E-mail: <a href={`mailto:${emailLgpd}`} className="text-kairos-orange hover:underline" data-testid="email-lgpd">{emailLgpd}</a>
                  </p>
                </div>
              </section>
            </article>

            {/* Back link */}
            <div className="mt-12 pt-8 border-t border-white/10">
              <Link to="/" className="inline-flex items-center gap-2 text-kairos-orange hover:text-kairos-orange-light transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Voltar para o início
              </Link>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-8 border-t border-white/5">
          <div className="container mx-auto px-4 text-center">
            <p className="text-gray-500 text-sm">
              © 2026 Seminário Psicoteológico Kairós. Todos os direitos reservados.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PoliticaPrivacidadePage;
